import { TRAINING_GATE_FACTIONS } from "@frontier/content/training";
import { FACTIONS, type FactionId } from "@frontier/content/factions";
import { regionRisk } from "@frontier/content/risk";
import { Container, Graphics, Text } from "pixi.js";
import type { GateSnapshot } from "@frontier/protocol/contracts";
interface GateView {
  factionId: FactionId | null;
  container: Container;
  ring: Graphics;
  label: Text;
  radius: number;
  color: number;
}

export class GateViews {
  readonly container = new Container();
  private readonly views = new Map<string, GateView>();

  update(gates: readonly GateSnapshot[]): void {
    const ids = new Set(gates.map((gate) => gate.id));
    for (const [id, view] of this.views) {
      if (ids.has(id)) continue;
      view.container.destroy({ children: true });
      this.views.delete(id);
    }
    for (const gate of gates) {
      let view = this.views.get(gate.id);
      if (!view) {
        const container = new Container({ label: `gate:${gate.id}` });
        const ring = new Graphics();
        const label = new Text({
          text: gate.name,
          style: {
            fontFamily: "system-ui, sans-serif",
            fontSize: 12,
            fill: 0x8edee1,
            letterSpacing: 1,
          },
        });
        label.anchor.set(0.5);
        container.addChild(ring, label);
        view = { container, ring, label, radius: gate.radius, color: 0x64d3eb, factionId: TRAINING_GATE_FACTIONS[gate.id] ?? null };
        this.views.set(gate.id, view);
        this.container.addChild(container);
      }
      view.container.position.set(gate.x, gate.y);
      view.radius = gate.radius;
      const risk = regionRisk(gate.toSectorId);
      view.color =
        risk === "open-pvp"
          ? 0xf29a82
          : risk === "risky-pve"
            ? 0xe8bf77
            : 0x64d3eb;
      if (view.factionId) {
        const faction = FACTIONS[view.factionId];
        view.color = Number.parseInt(faction.color.slice(1), 16);
        view.label.style.fontSize = 13;
        view.label.style.align = "center";
      }
      view.label.style.fill = view.color;
      view.label.text = view.factionId
        ? `ТЕЛЕПОРТ НА БАЗУ\n${FACTIONS[view.factionId].name}\nВЫБОР ФРАКЦИИ НАВСЕГДА`
        : `${gate.name}${risk === "open-pvp" ? " · PVP" : risk === "risky-pve" ? " · РИСК" : ""}`;
    }
  }

  render(now: number, zoom: number): void {
    for (const view of this.views.values()) {
      if (view.factionId) {
        this.renderFactionPortal(view, now, zoom);
        continue;
      }
      const radius = view.radius * 0.62;
      view.ring
        .clear()
        .circle(0, 0, radius)
        .fill({ color: 0x0f5975, alpha: 0.12 })
        .stroke({ color: view.color, width: 2, alpha: 0.5 });
      view.ring
        .circle(0, 0, view.radius)
        .stroke({ color: 0x5292a8, width: 1, alpha: 0.16 });
      for (let section = 0; section < 4; section += 1) {
        const angle = (section * Math.PI) / 2 + now / 5000;
        view.ring
          .arc(0, 0, radius + 8, angle, angle + 0.52)
          .stroke({ color: view.color, width: 3, alpha: 0.7 });
      }
      view.ring
        .poly([-8, -14, 9, 0, -8, 14])
        .stroke({ color: view.color, width: 2, alpha: 0.8 });
      view.label.scale.set(1 / zoom);
      view.label.y = view.radius + 21 / zoom;
    }
  }

  private renderFactionPortal(view: GateView, now: number, zoom: number): void {
    const glow = 0.08 + (Math.sin(now / 1100) + 1) * 0.025;
    const ring = view.ring.clear();
    ring.circle(0, 0, 147).fill({ color: view.color, alpha: glow * 0.3 });
    ring.circle(0, 0, 126).fill({ color: view.color, alpha: glow });
    ring.circle(0, 0, 99).fill({ color: 0x061322, alpha: 0.9 })
      .stroke({ color: view.color, width: 2, alpha: 0.8 });
    ring.circle(0, 0, 83).stroke({ color: view.color, width: 8, alpha: 0.12 });
    for (let index = 0; index < 6; index++) {
      const angle = index * Math.PI / 3 + now / 9000;
      ring.beginPath().arc(0, 0, 113, angle, angle + 0.6)
        .stroke({ color: view.color, width: 4, alpha: 0.7 });
      ring.beginPath().arc(0, 0, 71, -angle, -angle + 0.28)
        .stroke({ color: view.color, width: 2, alpha: 0.45 });
    }
    if (view.factionId === "concord") {
      ring.poly([-30, -17, 0, -34, 30, -17, 30, 17, 0, 34, -30, 17])
        .stroke({ color: view.color, width: 3 });
      ring.poly([-15, -2, -2, 11, 17, -13]).stroke({ color: view.color, width: 4 });
    } else if (view.factionId === "helion") {
      const star: number[] = [];
      for (let index = 0; index < 16; index++) {
        const angle = -Math.PI / 2 + index * Math.PI / 8;
        const radius = index % 2 === 0 ? 34 : 16;
        star.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
      }
      ring.poly(star).stroke({ color: view.color, width: 3 });
      ring.circle(0, 0, 8).stroke({ color: view.color, width: 2 });
    } else {
      ring.poly([0, -34, 32, 24, 0, 12, -32, 24]).stroke({ color: view.color, width: 3 });
    }
    view.label.scale.set(1 / zoom);
    view.label.y = 153 + 21 / zoom;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
